<div 
  class="blog-post-selector-overlay fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 animate-fadeIn"
  (keydown)="onEscapeKey($event)"
  role="dialog"
  aria-modal="true"
  [attr.aria-label]="getTitle()">
  
  <div 
    class="blog-post-selector-container bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-slideUp"
    (click)="$event.stopPropagation()">
    
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
        {{ getTitle() }}
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {{ selectedCount() }} post{{ selectedCount() !== 1 ? 's' : '' }} selected
        @if (maxSelection(); as max) {
          <span class="text-gray-500 dark:text-gray-500"> (max {{ max }})</span>
        }
      </p>
    </div>

    <!-- Search and Filters -->
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
      <!-- Quick Search Box (Always Visible) -->
      @if (isSearchEnabled()) {
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            [value]="searchTerm()"
            (input)="onSearchChange($any($event.target).value)"
            placeholder="Search by post title..."
            class="block w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all"
            aria-label="Search posts by title">
          @if (searchTerm()) {
            <button
              type="button"
              (click)="clearSearch()"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Clear search">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          }
        </div>
      }

      <!-- Phase 3: Advanced Filters Accordion Toggle -->
      <div class="flex items-center justify-between">
        <button
          type="button"
          (click)="toggleAdvancedFilters()"
          (keydown.enter)="toggleAdvancedFilters()"
          (keydown.space)="$event.preventDefault(); toggleAdvancedFilters()"
          class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          [attr.aria-expanded]="showAdvancedFilters()"
          aria-controls="advanced-filters-panel"
          aria-label="Toggle advanced filters">
          <!-- Chevron Icon -->
          <svg 
            class="h-5 w-5 transition-transform duration-300"
            [class.rotate-180]="showAdvancedFilters()"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
          <span>Advanced Filters</span>
          <!-- Active Filter Count Badge -->
          @if (activeFiltersCount() > 0) {
            <span 
              class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-blue-600 dark:bg-blue-500 rounded-full animate-scaleIn"
              [attr.aria-label]="activeFiltersCount() + ' active filter' + (activeFiltersCount() > 1 ? 's' : '')">
              {{ activeFiltersCount() }}
            </span>
          }
        </button>

        <!-- Clear All Filters Button (Only when filters active) -->
        @if (activeFiltersCount() > 0) {
          <button
            type="button"
            (click)="clearAllFilters()"
            class="text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 rounded px-2 py-1 animate-fadeIn"
            aria-label="Clear all active filters">
            Clear all filters
          </button>
        }
      </div>

      <!-- Phase 3: Advanced Filters Panel (Collapsible) -->
      <div 
        id="advanced-filters-panel"
        class="accordion-panel overflow-hidden min-h-11"
        [class.accordion-expanded]="showAdvancedFilters()"
        [attr.aria-hidden]="!showAdvancedFilters()">
        <div class="accordion-content pt-3 space-y-3">
          
          <!-- Status Filter -->
          @if (isStatusFilterEnabled()) {
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Publish Status
              </label>
              <div class="flex items-center gap-2 flex-wrap">
                @for (option of statusFilterOptions(); track option.value) {
                  <button
                    type="button"
                    (click)="onStatusFilterChange($any(option.value))"
                    [class]="selectedStatusFilter() === option.value 
                      ? 'bg-blue-600 text-white dark:bg-blue-500 ring-2 ring-blue-400 dark:ring-blue-300' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'"
                    class="px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    [attr.aria-pressed]="selectedStatusFilter() === option.value"
                    [attr.aria-label]="'Filter by ' + option.label + ' status'">
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          }

          <!-- Phase 4: Creation Date Range Filter -->
          <cms-date-range-filter
            [selectedPreset]="selectedDatePreset()"
            [dateFrom]="dateFrom()"
            [dateTo]="dateTo()"
            [label]="'Creation Date'"
            [presets]="DATE_PRESETS"
            (rangeChange)="onCreationDateRangeChange($event)"
            (clear)="clearCreationDateFilter()"
          />

          <!-- Phase 4: Update Date Range Filter -->
          <cms-date-range-filter
            [dateFrom]="updateFrom()"
            [dateTo]="updateTo()"
            [label]="'Last Updated'"
            [presets]="[]"
            (rangeChange)="onUpdateDateRangeChange($event)"
            (clear)="clearUpdateDateFilter()"
          />

        <!-- Phase 5: Author Filter -->
        <cms-author-filter
          [organizationId]="getOrganizationId()"
          [selectedAuthorId]="selectedAuthorId()"
          [label]="'Author'"
          [showSearch]="true"
          [searchThreshold]="5"
          (authorChange)="onAuthorChange($event)"
          (clear)="clearAuthorFilter()"
        />

        <!-- Phase 6: Post Type Filter -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Post Type</h3>
            @if (selectedPostTypeId()) {
              <button
                type="button"
                (click)="clearPostTypeFilter()"
                class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                [attr.aria-label]="ARIA_LABELS.CLEAR_POST_TYPE_FILTER">
                Clear
              </button>
            }
          </div>

          <!-- Reusable PostTypeDropdownComponent -->
          <cms-post-type-dropdown
            [organizationId]="getOrganizationId()"
            [selectedPostTypeId]="selectedPostTypeId()"
            [placeholder]="'All post types'"
            [showSearch]="true"
            [searchThreshold]="5"
            (selectionChange)="onPostTypeChange($event?.id || null, $event?.name)"
          />
        </div>

      </div>
      </div>

      <!-- Bulk Selection Actions -->
      @if (filteredPosts().length > 0) {
        <div class="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            (click)="selectAll()"
            [disabled]="isMaxSelectionReached() && selectedCount() === 0"
            class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Select all filtered posts">
            Select All
          </button>
          <span class="text-gray-300 dark:text-gray-600">|</span>
          <button
            type="button"
            (click)="deselectAll()"
            [disabled]="selectedCount() === 0"
            class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Deselect all posts">
            Deselect All
          </button>
        </div>
      }
    </div>

    <!-- Phase 7: Consolidated Results Counter & Active Filters -->
    @if (hasSearched() && !isLoading() && !showEmptyState()) {
      <div class="px-6 py-3 border-t border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div class="flex items-center justify-between">
          <!-- Results Count -->
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Showing {{ filteredPosts().length }} post{{ filteredPosts().length === 1 ? '' : 's' }}
            </span>
            @if (activeFiltersCount() > 0) {
              <span class="text-xs text-gray-500 dark:text-gray-400">
                ({{ activeFiltersCount() }} filter{{ activeFiltersCount() === 1 ? '' : 's' }} active)
              </span>
            }
          </div>

          <!-- Clear All Filters Button -->
          @if (activeFiltersCount() > 0) {
            <button
              type="button"
              (click)="clearAllFilters()"
              class="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
              [attr.aria-label]="ARIA_LABELS.CLEAR_ALL_FILTERS">
              Clear all filters
            </button>
          }
        </div>

        <!-- Active Filters Chips -->
        @if (activeFiltersCount() > 0) {
          <div class="flex items-center gap-2 flex-wrap mt-2">
            <!-- Status Filter Chip -->
            @if (selectedStatusFilter() !== 'all') {
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                Status: {{ getStatusLabel(selectedStatusFilter()) }}
                <button
                  type="button"
                  (click)="onStatusFilterChange('all')"
                  class="hover:text-blue-900 dark:hover:text-blue-200 focus:outline-none"
                  aria-label="Remove status filter">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </span>
            }

            <!-- Creation Date Filter Chip -->
            @if (dateFrom() || dateTo()) {
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                Created: {{ formatDateRange(dateFrom(), dateTo()) }}
                <button
                  type="button"
                  (click)="clearCreationDateFilter()"
                  class="hover:text-purple-900 dark:hover:text-purple-200 focus:outline-none"
                  aria-label="Remove creation date filter">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </span>
            }

            <!-- Update Date Filter Chip -->
            @if (updateFrom() || updateTo()) {
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Updated: {{ formatDateRange(updateFrom(), updateTo()) }}
                <button
                  type="button"
                  (click)="clearUpdateDateFilter()"
                  class="hover:text-amber-900 dark:hover:text-amber-200 focus:outline-none"
                  aria-label="Remove update date filter">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </span>
            }

            <!-- Author Filter Chip -->
            @if (selectedAuthorId()) {
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                Author: {{ getSelectedAuthorName() }}
                <button
                  type="button"
                  (click)="clearAuthorFilter()"
                  class="hover:text-green-900 dark:hover:text-green-200 focus:outline-none"
                  aria-label="Remove author filter">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </span>
            }

            <!-- Post Type Filter Chip -->
            @if (selectedPostTypeId()) {
              <span class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300">
                Type: {{ getSelectedPostTypeName() }}
                <button
                  type="button"
                  (click)="clearPostTypeFilter()"
                  class="hover:text-pink-900 dark:hover:text-pink-200 focus:outline-none"
                  aria-label="Remove post type filter">
                  <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </span>
            }
          </div>
        }
      </div>
    }

    <!-- Post List -->
    <div class="flex-1 overflow-y-auto px-6 py-4 min-h-0">
      <!-- Loading State -->
      @if (isLoading()) {
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <div class="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
          <p class="text-sm text-gray-600 dark:text-gray-400">Searching posts...</p>
        </div>
      }
      
      <!-- Phase 2: Threshold Exceeded Message -->
      @else if (showThresholdExceededMessage()) {
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <svg class="h-16 w-16 text-amber-500 dark:text-amber-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Too many posts to display
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 max-w-md mb-1">
            Your organization has <span class="font-semibold text-gray-900 dark:text-white">{{ totalAvailablePosts() }}+</span> blog posts.
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-500">
            Please use the search or filters above to narrow down your results.
          </p>
        </div>
      }
      
      <!-- Initial State (Before Search) -->
      @else if (showInitialState()) {
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <svg class="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Start Searching</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Type at least {{ getMinSearchLength() }} characters to search for blog posts
          </p>
        </div>
      }
      
      <!-- Empty State (After Search, No Results) -->
      @else if (showEmptyState()) {
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <svg class="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-1">No posts found</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search term or filters
          </p>
        </div>
      }
      
      <!-- Error State -->
      @else if (errorMessage()) {
        <div class="flex flex-col items-center justify-center py-12 text-center">
          <svg class="h-16 w-16 text-red-400 dark:text-red-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Error</h3>
          <p class="text-sm text-red-600 dark:text-red-400">{{ errorMessage() }}</p>
        </div>
      }
      
      <!-- Post Items -->
      @else if (filteredPosts().length > 0) {
        <!-- Phase 2: Initial Posts Info Message -->
        @if (showInitialPosts() && getShowThresholdMessage()) {
          <div class="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div class="flex items-start gap-2">
              <svg class="h-5 w-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-sm text-blue-800 dark:text-blue-300">
                {{ initialPostsMessage() }}
              </p>
            </div>
          </div>
        }
        
        <!-- Post Items -->
        <div class="space-y-2" role="list" aria-label="Blog posts list">
          @for (post of filteredPosts(); track post._id) {
            <div
              (click)="onRowClick(post._id, $event)"
              (keydown)="onRowKeydown(post._id, $event)"
              (mouseenter)="onRowMouseEnter(post._id)"
              (mouseleave)="onRowMouseLeave()"
              [class]="isPostSelected(post._id) 
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' 
                : hoveredPostId() === post._id 
                  ? 'bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'"
              class="flex items-center gap-4 p-4 border rounded-lg transition-all duration-200 cursor-pointer group"
              [class.opacity-50]="!canSelectPost(post._id)"
              [class.cursor-not-allowed]="!canSelectPost(post._id)"
              tabindex="0"
              role="listitem"
              [attr.aria-selected]="isPostSelected(post._id)"
              [attr.aria-disabled]="!canSelectPost(post._id)">
              
              <!-- Checkbox -->
              <div class="flex-shrink-0">
                <input
                  type="checkbox"
                  [id]="'post-' + post._id"
                  [checked]="isPostSelected(post._id)"
                  (change)="togglePost(post._id)"
                  [disabled]="!canSelectPost(post._id)"
                  class="h-5 w-5 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  [attr.aria-label]="'Select ' + post.title">
              </div>

              <!-- Post Content -->
              <div class="flex-1 min-w-0">
                <label 
                  [for]="'post-' + post._id"
                  class="block text-sm font-medium text-gray-900 dark:text-white truncate cursor-pointer group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {{ post.title }}
                </label>
              </div>

              <!-- Status Badge -->
              <div class="flex-shrink-0">
                <span [class]="getStatusBadgeClass(post.publishStatus)">
                  {{ getStatusDisplayText(post.publishStatus) }}
                </span>
              </div>

              <!-- Selection Indicator -->
              @if (isPostSelected(post._id)) {
                <div class="flex-shrink-0 text-blue-600 dark:text-blue-400 animate-scaleIn" aria-hidden="true">
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Footer Actions -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
      <button
        type="button"
        (click)="onCancel()"
        class="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200">
        {{ getSecondaryButtonText() }}
      </button>
      <button
        type="button"
        (click)="onConfirm()"
        [disabled]="selectedCount() === 0"
        class="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md">
        {{ getPrimaryButtonText() }}
        @if (selectedCount() > 0) {
          <span class="ml-1.5 bg-blue-700 dark:bg-blue-600 px-2 py-0.5 rounded-full text-xs">
            {{ selectedCount() }}
          </span>
        }
      </button>
    </div>
  </div>
</div>
